import { getFirmwareApiGateway } from 'common';

import {
  isFirmwareOverrideEnabled,
  getFirmwareOverrides,
  createMockAsyncState,
  shouldOverrideEndpoint,
  type FirmwareOverrides,
} from '../../utils/firmwareConfig';
import { HttpClient } from '../../utils/httpClient';
import { useAsyncState } from '../../utils/useAsyncState';

export interface Brightness {
  brightness: number;
}

interface UseBrightnessOptions {
  isOverride?: boolean;
  customOverrides?: FirmwareOverrides;
}

export function useBrightness(options?: UseBrightnessOptions) {
  const httpClient = new HttpClient(getFirmwareApiGateway());

  // Check if build-time override is enabled or manual override is requested
  const isOverrideMode = options?.isOverride || isFirmwareOverrideEnabled();
  const overrides = options?.customOverrides || getFirmwareOverrides();

  // Brightness - use override or API call
  const brightness =
    isOverrideMode && shouldOverrideEndpoint('/brightness') && overrides.brightness
      ? createMockAsyncState(overrides.brightness)
      : useAsyncState(() => httpClient.get<Brightness>('/brightness'));

  const setBrightness = async (value: number) => {
    if (isOverrideMode && shouldOverrideEndpoint('/brightness')) {
      // In override mode, simulate the API call without actually making it
      console.log(`🔧 [Override] Would set brightness to: ${value}`);
      return;
    }

    await httpClient.post('/brightness', { brightness: value });
    await brightness.execute(); // Refresh after setting
  };

  const dimBrightness = async () => {
    if (brightness.data?.brightness !== 20) {
      await setBrightness(20);
    }
  };

  const restoreBrightness = async () => {
    if (brightness.data?.brightness !== 100) {
      await setBrightness(100);
    }
  };

  return {
    brightness,
    setBrightness,
    dimBrightness,
    restoreBrightness,
  };
}
